import type { RiskAssessmentResultConfig } from '@risksmart-app/domain/src/types/risk-assessment-result-config';

type Rating = RiskAssessmentResultConfig['likelihood']['ratings'][number];

export interface MatrixGap {
  likelihood: Rating;
  impact: Rating;
}

const cellKey = (likelihood: number, impact: number): string =>
  `${likelihood}:${impact}`;

// The write schema enforces unique values, but stored configs predate it.
const uniqueByValue = (ratings: Rating[]): Rating[] => {
  const seen = new Map<number, Rating>();
  for (const rating of ratings) {
    if (!seen.has(rating.value)) {
      seen.set(rating.value, rating);
    }
  }

  return [...seen.values()];
};

/**
 * Coordinates the matrix must define but does not: the cross-product of the
 * declared likelihood and impact values, minus the cells present. Only
 * declared values are required — a scale of {1, 2, 5} needs 9 cells, not 25,
 * so the values a scale skips are not themselves gaps (RSP-7276).
 */
export const findMatrixGaps = (
  config: Pick<RiskAssessmentResultConfig, 'likelihood' | 'impact' | 'matrix'>
): MatrixGap[] => {
  const defined = new Set(
    config.matrix.map((cell) => cellKey(cell.likelihood, cell.impact))
  );
  const gaps: MatrixGap[] = [];
  for (const likelihood of uniqueByValue(config.likelihood.ratings)) {
    for (const impact of uniqueByValue(config.impact.ratings)) {
      if (!defined.has(cellKey(likelihood.value, impact.value))) {
        gaps.push({ likelihood, impact });
      }
    }
  }

  return gaps;
};
